import { PropertyMediaKind, type TCFile } from 'src/api/generated';
import { ALLOWED_FILE_TYPES } from 'src/modules/shared/components/atoms/inputs/file-input/const';

/**
 * Property-media kinds supported by the property editor.
 */
export type SupportedPropertyMediaKind = PropertyMediaKind;

const IMAGE_PREFIX = 'image/';
const VIDEO_PREFIX = 'video/';
const AUDIO_PREFIX = 'audio/';
const PROPERTY_MEDIA_DOCUMENT_TYPES = new Set([
  'application/pdf',
  'application/msword',
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
  'application/vnd.ms-excel',
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  'application/vnd.ms-powerpoint',
  'application/vnd.openxmlformats-officedocument.presentationml.presentation',
  'text/plain',
  'text/csv',
]);

/**
 * Infers the property-media kind from the file content type, falling back to
 * the persisted property-media kind when the content type is missing.
 *
 * @param contentType - MIME type reported by the Files service.
 * @param fallbackKind - Existing persisted media kind when available.
 * @returns The normalized property-media kind used by the editor UI.
 */
export const inferPropertyMediaKind = (
  contentType?: string | null,
  fallbackKind?: SupportedPropertyMediaKind | null
): SupportedPropertyMediaKind => {
  if (contentType?.startsWith(IMAGE_PREFIX)) {
    return PropertyMediaKind.IMAGE;
  }

  if (contentType?.startsWith(VIDEO_PREFIX)) {
    return PropertyMediaKind.VIDEO;
  }

  if (contentType?.startsWith(AUDIO_PREFIX)) {
    return PropertyMediaKind.AUDIO;
  }

  if (fallbackKind != null) {
    return fallbackKind;
  }

  return PropertyMediaKind.DOCUMENT;
};

/**
 * Resolves the property-media kind for a Files item plus optional persisted
 * fallback data.
 *
 * @param file - Files service item to inspect.
 * @param fallbackKind - Existing persisted property-media kind.
 * @returns The normalized property-media kind.
 */
export const inferPropertyMediaKindFromFile = (
  file: Pick<TCFile, 'contentType'>,
  fallbackKind?: SupportedPropertyMediaKind | null
): SupportedPropertyMediaKind =>
  inferPropertyMediaKind(file.contentType, fallbackKind);

/**
 * Returns the HTML `accept` value used by the property media uploader.
 *
 * @returns A comma-separated MIME-type list supported by the shared Files input rules.
 */
export const getPropertyMediaAcceptValue = (): string =>
  Object.keys(ALLOWED_FILE_TYPES)
    .filter(
      (contentType) =>
        contentType.startsWith(IMAGE_PREFIX) ||
        contentType.startsWith(VIDEO_PREFIX) ||
        contentType.startsWith(AUDIO_PREFIX) ||
        PROPERTY_MEDIA_DOCUMENT_TYPES.has(contentType)
    )
    .join(',');
